import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateId() {
  return Math.random().toString(36).substring(2, 9)
}

// Helper types for price calculation
interface UserSetting {
  id: string
  configurator_type: string
  price_multiplier: number
  enabled: boolean
}

/**
 * Calculate the display price based on presentation mode and user settings
 * @param originalPrice - The original price before any multipliers
 * @param configuratorTypeId - The configurator type ID to find the matching setting
 * @param userSettings - Array of user-specific price multiplier settings
 * @param isPresentationMode - Whether presentation mode is active
 * @returns The price to display (original or multiplied)
 */
export function getDisplayPrice(
  originalPrice: number,
  configuratorTypeId: string,
  userSettings: UserSetting[],
  isPresentationMode: boolean
): number {
  // If not in presentation mode, return original price
  if (!isPresentationMode) {
    return originalPrice
  }

  // Find the setting that matches the configurator type
  const matchingSetting = userSettings.find(
    (setting) => setting.configurator_type === configuratorTypeId && setting.enabled
  )

  // If matching setting found and has valid multiplier, apply it
  if (matchingSetting && typeof matchingSetting.price_multiplier === 'number' && matchingSetting.price_multiplier > 0) {
    return originalPrice * matchingSetting.price_multiplier
  }

  // Default to original price if no matching setting or invalid multiplier
  return originalPrice
}

/**
 * Format a price as currency string
 * @param price - The price to format
 * @param currency - Currency code (default: 'EUR')
 * @param locale - Locale for formatting (default: 'de-DE')
 * @returns Formatted currency string
 */
export function formatCurrency(price: number, currency: string = 'EUR', locale: string = 'de-DE'): string {
  return new Intl.NumberFormat(locale, { 
    style: 'currency', 
    currency: currency 
  }).format(price)
}

